import { Formik, FormikProps } from 'formik'
import { FunctionComponent, useContext, useState } from 'react'
import { useAtomValue } from 'jotai'
import {
  AddressFields,
  FieldWrapper,
  FormBlock,
  FormFooter,
  FormikPhoneInput,
  FormikSelectInput,
  FormikSwitch,
  FormikTextInput,
  GridLayout,
  MedicalForm,
  RoundedButton,
} from '../../shared'
import { Patient } from '../../../model/Patient'
import { patientFormToPatientMapper } from './PatientForm.mapper'
import { EMPTY_DEFAULT_VALUES, PatientFormFields, PatientFormProps } from './PatientForm.model'
import { getValidationSchema } from './PatientForm.validator'
import { PatientContactsFormBlock } from './PatientContactsFormBlock'
import { getEditorEmptyJsonValue, JSONToValue, SimpleEditor, valueToJSON } from '@follow/farte'
import { NOTES_TOOLBAR_CONFIG } from '../../../model/Notes'
import { IdentityStatusBlock } from './IdentityStatusBlock'
import { getInitialValues } from './PatientForm.utils'
import { PatientIdentityBlock } from './PatientIdentityBlock'
import { HealthInformationBlock } from './HealthInformationBlock'
import { useCreatePatient, useUpdatePatient } from '../../../hooks/queries/patients'
import { useNavigate } from 'react-router-dom'
import { DoctolibPatientInfoUpdateModal } from '../PatientInfos/PatientGeneralInfos/DoctolibPatientInfoUpdateModal'
import { useGetPendingFollowPatient } from '../../../hooks/queries/doctolib'
import { useDoctolibPendingChangesActions } from '../../../hooks/utils/doctolib'
import styles from './PatientForm.module.scss'
import { useGetAssigningAuthorities } from '../../../hooks/queries/assigningAuthorities'
import { isDoctolibLoaded } from '../../../state/doctolib'
import { useQueryClient } from '@tanstack/react-query'
import { recipientSuggestionsKeys } from '../../../hooks/queries/recipientSuggestions/recipientSuggestions.keys'
import { ConnectedUserContext } from '../../../misc/auth.utilities'
import { isSuperAdmin } from '../../../misc/user.utilities'
import { useGoBack } from '../../../hooks/utils'

export const PatientForm: FunctionComponent<PatientFormProps> = ({
  patient,
  impersonatePermissions,
  enabledFeatures,
  addError,
}) => {
  const { loggedUser } = useContext(ConnectedUserContext)
  const { mutate: createPatient, isPending: creatingPatient } = useCreatePatient()
  const { mutate: updatePatient, isPending: updatingPatient } = useUpdatePatient()
  const { handleClose, handleSubmit: handleSubmitDoctolibPendingChanges } =
    useDoctolibPendingChangesActions(patient)
  const navigate = useNavigate()
  const goBack = useGoBack(patient?.id ? `/patients/${patient.id}` : '/patients')
  const isZipperLoaded = useAtomValue(isDoctolibLoaded)
  const queryClient = useQueryClient()

  const {
    query: { data: pendingDoctolibPatientData = [] },
  } = useGetPendingFollowPatient({
    patientId: patient?.id,
    enabled: !!patient?.isEditable && isZipperLoaded,
  })
  const {
    query: { data: assigningAuthorities },
  } = useGetAssigningAuthorities()

  const [localNotesValue, setLocaleNotesValue] = useState(
    JSONToValue((patient && patient.note) || getEditorEmptyJsonValue()),
  )
  const initialValues = !!patient ? getInitialValues(patient) : EMPTY_DEFAULT_VALUES

  const hasAccessToInsOid = impersonatePermissions?.permissions.insOidAccess ?? true
  const isInsiFeatureEnabled: boolean = !!enabledFeatures?.insiValidation

  const handleSubmit = (values: PatientFormFields) => {
    const updatedPatient: Partial<Patient> = patientFormToPatientMapper(values, hasAccessToInsOid)

    if (patient && patient.id) {
      updatePatient(
        {
          id: patient.id,
          payload: { ...updatedPatient, note: valueToJSON(localNotesValue) },
        },
        {
          onSuccess: () => {
            if (patient.disallowCitizenMSSMessaging !== updatedPatient.disallowCitizenMSSMessaging)
              queryClient.invalidateQueries({ queryKey: recipientSuggestionsKeys.all })

            // Back where the form was opened from, filters included: navigate(-1) was a dead end
            // after a page reload
            goBack()
          },
          onError: (err) => {
            console.error(err)
          },
        },
      )
    } else {
      createPatient(
        { patient: { ...updatedPatient, note: valueToJSON(localNotesValue) } },
        { onSuccess: (patient) => navigate(`/patients/${patient.id}`, { replace: true }) },
      )
    }
  }

  const displayCustomFields = initialValues.customFields.length > 0

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        validateOnMount={true}
        validateOnChange={isSuperAdmin(loggedUser) ? false : true}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialTouched={{
          title: patient !== null,
        }}
      >
        {(props: FormikProps<PatientFormFields>) => (
          <MedicalForm onSubmit={props.handleSubmit}>
            <GridLayout
              columns={2}
              rowsTemplate="auto"
              alignColumns="start"
              gap="medium"
              padding="medium"
            >
              <PatientIdentityBlock patient={patient} />
              <FormBlock icon="infoCircle" label="Coordonnées">
                <AddressFields type="patient" colorPreset="light" />
                <div className={styles.patientFormTelephones}>
                  <div className={styles.patientFormTelephone1}>
                    <FormikPhoneInput
                      fieldName="telephone1"
                      label="Téléphone mobile"
                      autocomplete={false}
                      colorPreset="light"
                      testId="patient-telephone1-enter"
                    />
                  </div>
                  <div>
                    <FormikPhoneInput
                      fieldName="telephone2"
                      label="Téléphone fixe"
                      autocomplete={false}
                      colorPreset="light"
                      testId="patient-telephone2-enter"
                    />
                  </div>
                </div>
                <FormikTextInput
                  fieldName="email"
                  label="E-mail"
                  autocomplete={false}
                  colorPreset="light"
                  testId="patient-email-enter"
                />
                <div className={styles.patientFormMssConsent}>
                  <FormikSwitch
                    disabled={!props.values.inseeNumber}
                    theme="primary"
                    label="Non consentement MSS Professionnelle"
                    infos={
                      !props.values.inseeNumber
                        ? 'Paramètre non modifiable : matricule INS requis'
                        : undefined
                    }
                    borderBottom
                    fieldName="disallowProfessionalMSSMessaging"
                    name="Le patient refuse le partage de ses informations via MSS Professionelle"
                    testId="switch-patient-professional_mss_messaging-disallow"
                  />
                </div>
                <div className={styles.patientFormMssConsent}>
                  <FormikSwitch
                    disabled={!props.values.inseeNumber}
                    theme="primary"
                    label="Non consentement MSS Citoyenne"
                    infos={
                      !props.values.inseeNumber
                        ? 'Paramètre non modifiable : matricule INS requis'
                        : undefined
                    }
                    borderBottom
                    fieldName="disallowCitizenMSSMessaging"
                    name="Le patient refuse le partage de ses informations via MSS Citoyenne"
                    testId="switch-patient-citizen_mss_messaging-disallow"
                  />
                </div>
              </FormBlock>

              <HealthInformationBlock
                assigningAuthorities={assigningAuthorities ?? []}
                hasAccessToInsOid={hasAccessToInsOid}
                isInsiFeatureEnabled={isInsiFeatureEnabled}
              />

              <IdentityStatusBlock
                impersonatePermissions={impersonatePermissions}
                patient={patient}
              />

              <FormBlock icon="clipboard" label="Notes">
                <FieldWrapper>
                  <SimpleEditor
                    value={localNotesValue}
                    onValueChange={setLocaleNotesValue}
                    toolbarConfig={NOTES_TOOLBAR_CONFIG}
                  />
                </FieldWrapper>
              </FormBlock>
              <FormBlock icon="at" label="Correspondants">
                <PatientContactsFormBlock
                  patientAddress={props.values.address}
                  patientId={patient?.id}
                  colorPreset="light"
                  addError={addError}
                />
              </FormBlock>
              {displayCustomFields && (
                <FormBlock icon="documentText" label="Personnalisé">
                  {initialValues.customFields.map((field, index) => {
                    if (field.type === 'TYPE_TEXT') {
                      return (
                        <FormikTextInput
                          key={index}
                          fieldName={`customFields[${index}].value`}
                          label={field.label}
                          colorPreset="light"
                          testId={`input-patient-${field.uuid}`}
                        />
                      )
                    }
                    if (field.type === 'TYPE_OPTIONS') {
                      return (
                        <FormikSelectInput
                          key={index}
                          fieldName={`customFields[${index}].value`}
                          label={field.label}
                          options={[]}
                          colorPreset="light"
                          value={{ value: '', label: '' }}
                          testId={`select-patient-${field.uuid}`}
                        />
                      )
                    }
                    return null
                  })}
                </FormBlock>
              )}
            </GridLayout>
            <FormFooter>
              <div className={styles.patientFormSubmitButtonBox}>
                <RoundedButton
                  type="submit"
                  label={
                    patient && patient.id
                      ? updatingPatient
                        ? 'Modification du patient...'
                        : 'Modifier le patient'
                      : creatingPatient
                        ? 'Création du patient...'
                        : 'Créer le patient'
                  }
                  theme="primary"
                  disabled={!props.isValid || creatingPatient || updatingPatient}
                  testId={`button-patient-${patient && patient.id ? 'edit' : 'create'}`}
                />
              </div>
            </FormFooter>
          </MedicalForm>
        )}
      </Formik>
      <DoctolibPatientInfoUpdateModal
        display={!!pendingDoctolibPatientData.length}
        onSubmit={handleSubmitDoctolibPendingChanges}
        onClose={handleClose}
        pendingPatientData={pendingDoctolibPatientData}
      />
    </>
  )
}
